import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { publicAPI, lessonPlanAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { BookOpen, Copy, Home } from 'lucide-react';
import { toast } from 'sonner';

const SharedPlan = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [duplicating, setDuplicating] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await publicAPI.getSharedPlan(token);
        setPlan(data);
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Link expired or not found');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token]);

  const handleDuplicate = async () => {
    if (!user) {
      toast.info('Please log in to duplicate');
      navigate(`/login?redirect=/shared/${token}`);
      return;
    }
    setDuplicating(true);
    try {
      const { data } = await lessonPlanAPI.duplicate(plan.lesson_plan_id);
      toast.success('Copied to your plans');
      navigate(`/builder/${data.lesson_plan_id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate');
    } finally {
      setDuplicating(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C53030]" />
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="min-h-screen bg-[#FDFCFB] flex items-center justify-center">
        <Card className="p-10 text-center border border-stone-200 max-w-md">
          <BookOpen className="w-10 h-10 mx-auto text-stone-300 mb-3" />
          <p className="text-stone-600 mb-4">This shared plan is unavailable or the link has expired.</p>
          <Button data-testid="shared-plan-home" onClick={() => navigate('/')}>
            <Home className="w-4 h-4 mr-2" /> Go Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFCFB]">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-stone-200/50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <BookOpen className="w-6 h-6 text-[#C53030]" />
            <h1 className="text-lg font-bold text-[#1C1917]">Shared Lesson Plan</h1>
          </div>
          <Button
            data-testid="shared-plan-duplicate-btn"
            onClick={handleDuplicate}
            disabled={duplicating}
            className="bg-[#C53030] hover:bg-[#9B2C2C] text-white"
          >
            <Copy className="w-4 h-4 mr-2" />
            {duplicating ? 'Duplicating...' : 'Duplicate to My Plans'}
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-6">
          <span className="text-xs uppercase tracking-wider text-[#C53030] bg-red-50 px-2 py-1 rounded font-semibold mr-2">
            {plan.class_name}
          </span>
          <span className="text-sm text-stone-500">{plan.subject}</span>
          <h2 data-testid="shared-plan-chapter" className="text-3xl tracking-tight text-[#1C1917] mt-3">
            {plan.chapter || 'Untitled Chapter'}
          </h2>
          <p className="text-sm text-stone-500 mt-1">Shared anonymously • {plan.date_range}</p>
        </div>

        <div className="space-y-6">
          {(plan.days || []).map((d, idx) => (
            <Card key={`shared-day-${d.day}-${idx}`} data-testid={`shared-day-${idx}`} className="p-6 border border-stone-200">
              <p className="text-sm font-bold text-[#C53030] uppercase tracking-wide mb-3">{d.day}</p>
              {d.outcomes && <Section title="Learning Outcomes" text={d.outcomes} />}
              {d.curriculum_goals && <Section title="Curriculum Goals (NCFSE 2023)" text={d.curriculum_goals} />}
              {d.teacher_activity && <Section title="Teacher Activity" text={d.teacher_activity} />}
              {d.student_activity && <Section title="Student Activity" text={d.student_activity} />}
              {d.skills && <Section title="Skills" text={d.skills} />}
              {d.innovative_practices && <Section title="Innovative Practices" text={d.innovative_practices} />}
              {d.exit_card && <Section title="Exit Card" text={d.exit_card} highlight />}
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
};

const Section = ({ title, text, highlight }) => (
  <div className={`mb-3 ${highlight ? 'bg-amber-50 border-l-4 border-amber-400 pl-3 py-2 rounded-r' : ''}`}>
    <p className="text-xs font-semibold text-stone-500 uppercase tracking-wide mb-1">{title}</p>
    <p className="text-sm text-stone-800 whitespace-pre-line">{text}</p>
  </div>
);

export default SharedPlan;
